import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  Divider,
  Grid,
  Input,
  Radio,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../../../api/client';

const { Title, Text } = Typography;

const mergeByKey = (defaults, incoming, keyField = 'key') => {
  if (!Array.isArray(incoming) || incoming.length === 0) return defaults;
  const map = new Map(incoming.map((r) => [r[keyField], r]));
  return defaults.map((d) => ({ ...d, ...(map.get(d[keyField]) || {}) }));
};

export const createEmptyCotizacionFinal = (opts = {}) => ({
  fecha: new Date().toISOString().slice(0, 10),
  numeroCotizacion: '',
  contactoGlobal: '',
  contactoCliente: '',
  telefono: '',
  razonSocial: '',
  cuit: '',
  adminNombre: '',
  adminTelefono: '',
  adminMail: '',
  gestiones: {
    inscripcionImpexp: false,
    registroCamara: false,
    habilitacionBalanza: false,
    hablExpl: false,
    hablExplTercero: false,
    renovacionBalanza: false,
  },
  tipoOperacion: opts.tipoOperacion ?? 'EXPORTACION',
  sectorDocComercial: [
    { key: 'SET_COMPLETO', label: 'SET COMPLETO', si: false, no: false, comentarios: '' },
    { key: 'ORIGEN', label: 'ORIGEN', si: false, no: false, comentarios: '' },
    { key: 'FITOSANITARIO', label: 'FITOSANITARIO', si: false, no: false, comentarios: '' },
    {
      key: 'ENVIO_MUESTRAS_DHL',
      label: 'ENVIO DE MUESTRAS CTA DHL GLOBAL',
      si: false,
      no: false,
      comentarios: '',
    },
    {
      key: 'ENVIO_DOC_DHL',
      label: 'ENVIO DE DOCUM. CTA DHL GLOBAL',
      si: false,
      no: false,
      comentarios: '',
    },
  ],
  otrosConceptos: [
    { key: 'LOGISTICA', label: 'LOGÍSTICA', si: false, no: false, comentarios: '' },
    { key: 'SIRA', label: 'SIRA', si: false, no: false, comentarios: '' },
    {
      key: 'OTROS_DESPACHANTES',
      label: 'OTROS DESPACHANTES',
      si: false,
      no: false,
      comentarios: '',
    },
  ],
  servicioAta: [
    { key: 'COBRAR_CLIENTE', label: 'COBRAR CLIENTE', si: false, no: false },
    { key: 'COBRAR_TRANSPORTE', label: 'COBRAR TRANSPORTE', si: false, no: false },
  ],
  condicionesEspeciales: '',
  honorariosGlobal: '',
  condicionesPagoMoneda: '',
});

const normalizeFromApi = (api, defaultTipoOperacion = 'EXPORTACION') => {
  const base = createEmptyCotizacionFinal({ tipoOperacion: defaultTipoOperacion });
  if (!api) return base;
  return {
    ...base,
    ...api,
    fecha: api.fecha != null ? String(api.fecha).slice(0, 10) : base.fecha,
    tipoOperacion: api.tipoOperacion ?? defaultTipoOperacion,
    gestiones: { ...base.gestiones, ...(api.gestiones || {}) },
    sectorDocComercial: mergeByKey(base.sectorDocComercial, api.sectorDocComercial),
    otrosConceptos: mergeByKey(base.otrosConceptos, api.otrosConceptos),
    servicioAta: mergeByKey(base.servicioAta, api.servicioAta),
  };
};

const GESTIONES_LABELS = [
  ['inscripcionImpexp', 'Inscripción IMP/EXP'],
  ['registroCamara', 'Registro en Cámara'],
  ['habilitacionBalanza', 'Habilitación Balanza'],
  ['hablExpl', 'HABL EXPL'],
  ['hablExplTercero', 'HABL EXPL TERCERO'],
  ['renovacionBalanza', 'Renovación Balanza'],
];

/** Texto inicial para Honorarios Global: producto + líneas de la pre-cotización con concepto "muestra en grilla" = sí */
const buildHonorariosDesdePreCotizacion = (ini, catalogoConceptos) => {
  const cat = catalogoConceptos || [];
  const partes = [];
  if (ini.productoNombre) {
    partes.push(`Producto: ${ini.productoNombre}`);
  }
  const lineasGrilla = (ini.conceptos || []).filter((linea) => {
    if (linea.conceptoId == null) return false;
    const c = cat.find((x) => x.id === linea.conceptoId);
    return c && c.muestraEnGrilla !== false;
  });
  if (lineasGrilla.length) {
    if (partes.length) partes.push('');
    lineasGrilla.forEach((linea) => {
      const titulo = linea.nombre || 'Concepto';
      const det = linea.detalle != null && String(linea.detalle).trim() ? String(linea.detalle).trim() : '';
      partes.push(det ? `${titulo}: ${det}` : titulo);
    });
  }
  return partes.join('\n').trim();
};

const vacio = (v) => v == null || String(v).trim() === '';

/** Completa nombre/tel/mail de administración desde el ABM Cliente cuando el formulario no los tiene */
const mergeContactoAdminDesdeCliente = (base, cli) => {
  if (!cli) return base;
  return {
    ...base,
    adminNombre: vacio(base.adminNombre) ? (cli.adminNombre || '') : base.adminNombre,
    adminTelefono: vacio(base.adminTelefono) ? (cli.adminTelefono || '') : base.adminTelefono,
    adminMail: vacio(base.adminMail) ? (cli.adminMail || '') : base.adminMail,
  };
};

const CotizacionFinalForm = ({
  cotId,
  onBack,
  onSaved,
  defaultTipoOperacion = 'EXPORTACION',
  serviciosAcordadosDisabled = false,
}) => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => createEmptyCotizacionFinal({ tipoOperacion: defaultTipoOperacion }));
  /** Para nombre de archivo al descargar PDF (expo/impo + número visible). */
  const [pdfMeta, setPdfMeta] = useState({ nro: null, tipoOperacion: defaultTipoOperacion });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [iniRes, finRes, conRes] = await Promise.all([
          api.get(`/cotizaciones/${cotId}`),
          api.get(`/cotizaciones/${cotId}/cotizacion-final`),
          api.get('/conceptos', { params: { page: 1, limit: 500 } }),
        ]);
        if (cancelled) return;
        const ini = iniRes.data;
        if (!ini.aprobada) {
          message.error('La cotizacion debe estar aprobada');
          onBack();
          return;
        }
        setPdfMeta({
          nro: ini.numeroSecuencial ?? ini.id,
          tipoOperacion: ini.tipoOperacion ?? defaultTipoOperacion,
        });
        const catalogoConceptos = conRes.data?.items || [];
        const fin = finRes.data?.cotizacionFinal;

        let clienteDetalle = null;
        if (ini.numeroCliente != null) {
          try {
            const { data } = await api.get(`/clientes/${ini.numeroCliente}`);
            clienteDetalle = data;
          } catch {
            clienteDetalle = null;
          }
        }

        if (fin) {
          let merged = normalizeFromApi(fin, defaultTipoOperacion);
          if (serviciosAcordadosDisabled && defaultTipoOperacion === 'IMPORTACION') {
            merged = { ...merged, tipoOperacion: 'IMPORTACION' };
          }
          setForm(mergeContactoAdminDesdeCliente(merged, clienteDetalle));
        } else {
          setForm(
            mergeContactoAdminDesdeCliente(
              {
                ...createEmptyCotizacionFinal({ tipoOperacion: defaultTipoOperacion }),
                razonSocial: ini.clienteNombre || '',
                cuit: ini.cuit || '',
                numeroCotizacion: String(ini.numeroSecuencial ?? ini.id).padStart(3, '0'),
                honorariosGlobal: buildHonorariosDesdePreCotizacion(ini, catalogoConceptos),
              },
              clienteDetalle
            )
          );
        }
      } catch (e) {
        if (!cancelled) {
          message.error(e?.response?.data?.message || 'Error al cargar datos');
          onBack();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cotId, onBack, defaultTipoOperacion, serviciosAcordadosDisabled]);

  const setGest = (key, value) => {
    setForm((f) => ({ ...f, gestiones: { ...f.gestiones, [key]: value } }));
  };

  const patchRow = (section, index, patch) => {
    setForm((f) => {
      const arr = [...f[section]];
      arr[index] = { ...arr[index], ...patch };
      return { ...f, [section]: arr };
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const payload =
        serviciosAcordadosDisabled && defaultTipoOperacion === 'IMPORTACION'
          ? { ...form, tipoOperacion: 'IMPORTACION' }
          : form;
      await api.put(`/cotizaciones/${cotId}/cotizacion-final`, payload);
      message.success('Cotizacion final guardada');
      onSaved?.();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const descargarPdf = useCallback(async () => {
    try {
      const res = await api.get(`/cotizaciones/${cotId}/cotizacion-final/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const tag = pdfMeta.tipoOperacion === 'IMPORTACION' ? 'impo' : 'expo';
      const n = pdfMeta.nro ?? cotId;
      a.download = `cotizacion-final-${tag}-${n}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al generar PDF (guarde antes si aun no lo hizo)');
    }
  }, [cotId, pdfMeta]);

  /** Tablas SI/NO/comentarios: deshabilitadas en flujo importación o si el usuario elige «Importación» en Exportación. */
  const tablasServiciosDisabled =
    serviciosAcordadosDisabled || form.tipoOperacion === 'IMPORTACION';

  const colsDoc = (section) => [
    {
      title: 'Concepto',
      dataIndex: 'label',
      width: 260,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: 'SI',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form[section][i].si}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow(section, i, { si: v, no: v ? false : form[section][i].no });
          }}
        />
      ),
    },
    {
      title: 'NO',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form[section][i].no}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow(section, i, { no: v, si: v ? false : form[section][i].si });
          }}
        />
      ),
    },
    {
      title: 'Comentarios',
      render: (_, __, i) => (
        <Input
          disabled={tablasServiciosDisabled}
          value={form[section][i].comentarios}
          onChange={(e) => patchRow(section, i, { comentarios: e.target.value })}
          placeholder="Opcional"
        />
      ),
    },
  ];

  const colsAta = [
    {
      title: 'Concepto',
      dataIndex: 'label',
      width: 260,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: 'SI',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form.servicioAta[i].si}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow('servicioAta', i, { si: v, no: v ? false : form.servicioAta[i].no });
          }}
        />
      ),
    },
    {
      title: 'NO',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form.servicioAta[i].no}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow('servicioAta', i, { no: v, si: v ? false : form.servicioAta[i].si });
          }}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
              Volver
            </Button>
            <Title level={5} style={{ margin: 0, color: '#198039' }}>
              Cotizacion final — Condiciones de venta (COM-P-01)
            </Title>
          </Space>
        }
        extra={
          <Space wrap>
            <Button icon={<FilePdfOutlined />} onClick={() => void descargarPdf()}>
              PDF
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void guardar()}>
              Guardar
            </Button>
          </Space>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Complete el formulario según el acuerdo con el cliente. Podrá imprimir un PDF con el mismo contenido.
        </Text>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card size="small" title="Datos generales" style={{ borderColor: '#d8e8dc' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space wrap>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    Fecha
                  </Text>
                  <Input
                    type="date"
                    value={form.fecha || ''}
                    onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
                    style={{ width: 160 }}
                  />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    Cotización N°
                  </Text>
                  <Input
                    value={form.numeroCotizacion}
                    onChange={(e) => setForm((f) => ({ ...f, numeroCotizacion: e.target.value }))}
                    placeholder="Ej: 016"
                    style={{ width: 120 }}
                  />
                </div>
              </Space>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Contacto Global
                </Text>
                <Input
                  value={form.contactoGlobal}
                  onChange={(e) => setForm((f) => ({ ...f, contactoGlobal: e.target.value }))}
                  placeholder="Nombre"
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Contacto Cliente
                </Text>
                <Input
                  value={form.contactoCliente}
                  onChange={(e) => setForm((f) => ({ ...f, contactoCliente: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Teléfono
                </Text>
                <Input
                  value={form.telefono}
                  onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
                />
              </div>
            </Space>
          </Card>

          <Card size="small" title="Datos cliente" style={{ borderColor: '#d8e8dc' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Razón social
                </Text>
                <Input
                  value={form.razonSocial}
                  onChange={(e) => setForm((f) => ({ ...f, razonSocial: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  N° CUIT
                </Text>
                <Input value={form.cuit} onChange={(e) => setForm((f) => ({ ...f, cuit: e.target.value }))} />
              </div>
              <Divider style={{ margin: '8px 0' }}>Contacto administración y facturación</Divider>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Nombre
                </Text>
                <Input
                  value={form.adminNombre}
                  onChange={(e) => setForm((f) => ({ ...f, adminNombre: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Teléfono
                </Text>
                <Input
                  value={form.adminTelefono}
                  onChange={(e) => setForm((f) => ({ ...f, adminTelefono: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Mail
                </Text>
                <Input
                  value={form.adminMail}
                  onChange={(e) => setForm((f) => ({ ...f, adminMail: e.target.value }))}
                />
              </div>
            </Space>
          </Card>

          <Card size="small" title="Gestiones solicitadas por el cliente" style={{ borderColor: '#d8e8dc' }}>
            <Space direction="vertical" size="small">
              {GESTIONES_LABELS.map(([key, label]) => (
                <Checkbox
                  key={key}
                  checked={form.gestiones[key]}
                  onChange={(e) => setGest(key, e.target.checked)}
                >
                  {label}
                </Checkbox>
              ))}
            </Space>
          </Card>

          <Card size="small" title="Servicios acordados" style={{ borderColor: '#d8e8dc' }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Operación
            </Text>
            <ConfigProvider
              theme={{
                token: { colorPrimary: '#198039' },
                components: {
                  Radio: {
                    buttonBg: '#ffffff',
                    buttonCheckedBg: '#f0faf2',
                    buttonCheckedColor: '#198039',
                    buttonCheckedBorderColor: '#198039',
                    buttonColor: 'rgba(0,0,0,0.88)',
                    colorBorder: '#d9d9d9',
                  },
                },
              }}
            >
              <Radio.Group
                optionType="button"
                buttonStyle="outline"
                disabled={serviciosAcordadosDisabled}
                value={form.tipoOperacion ?? defaultTipoOperacion}
                onChange={(e) => setForm((f) => ({ ...f, tipoOperacion: e.target.value }))}
              >
                <Radio.Button value="IMPORTACION">Importación</Radio.Button>
                <Radio.Button value="EXPORTACION">Exportación</Radio.Button>
              </Radio.Group>
            </ConfigProvider>
            <Divider />
            <Text strong>Sector documentación comercial</Text>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              columns={colsDoc('sectorDocComercial')}
              dataSource={form.sectorDocComercial}
              scroll={{ x: 600 }}
            />
            <Divider />
            <Text strong>Otros conceptos a cobrar</Text>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              columns={colsDoc('otrosConceptos')}
              dataSource={form.otrosConceptos}
              scroll={{ x: 600 }}
            />
            <Divider />
            <Text strong>Servicio ATA</Text>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              columns={colsAta}
              dataSource={form.servicioAta}
              scroll={{ x: 500 }}
            />
          </Card>

          <Card size="small" title="Condiciones especiales" style={{ borderColor: '#d8e8dc' }}>
            <Input.TextArea
              rows={6}
              value={form.condicionesEspeciales}
              onChange={(e) => setForm((f) => ({ ...f, condicionesEspeciales: e.target.value }))}
              placeholder="Delegaciones AFIP u observaciones"
            />
          </Card>

          <Card size="small" title="Honorarios Global" style={{ borderColor: '#d8e8dc' }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Información disponible solamente para administración
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Al cargar por primera vez se incorpora el nombre del producto y el detalle de los conceptos
              marcados con &quot;muestra en grilla&quot; en la cotización inicial.
            </Text>
            <Input.TextArea
              rows={6}
              value={form.honorariosGlobal}
              onChange={(e) => setForm((f) => ({ ...f, honorariosGlobal: e.target.value }))}
            />
          </Card>

          <Card size="small" title="Condiciones de pago / moneda" style={{ borderColor: '#d8e8dc' }}>
            <Input.TextArea
              rows={3}
              value={form.condicionesPagoMoneda}
              onChange={(e) => setForm((f) => ({ ...f, condicionesPagoMoneda: e.target.value }))}
            />
          </Card>

          <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={() => void guardar()}>
            Guardar cotizacion final
          </Button>
        </Space>
      </Card>
    </Spin>
  );
};

export default CotizacionFinalForm;
